import React, {
  Children,
  isValidElement,
  cloneElement,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import ParticleCell, {ParticleCellProps} from './ParticleCell';
import {Dimensions, Image, ImageProps} from 'react-native'; // Assuming ImageProps is imported or defined accordingly
import Particles, {initParticlesEngine} from '@tsparticles/react';
import {Engine, MoveDirection, OutMode} from '@tsparticles/engine';
// @ts-ignore
import {loadFull} from 'tsparticles';

type ParticleViewProps = {
  emitterPosition: {x: number; y: number};
  emitterZPosition: number;
  emitterShape: 'point' | 'line' | 'rectangle' | 'circle' | 'cuboid' | 'sphere';
  emitterSize: {width: number; height: number};
  emitterMode: 'points' | 'outline' | 'surface' | 'volume';
  renderMode: 'unordered' | 'oldestFirst' | 'oldestLast' | 'backToFront' | 'additive'; // Unsupported by tsParticles
  emitterDepth: number; // Unsupported by tsParticles
  preservesDepth: boolean; // Unsupported by tsParticles
  beginTimeOffset: number; // Unused
  useCurrentMediaTime: boolean; // Unsupported by tsParticles
  birthRate: number;
  lifetime: number;
  velocity: number;
  scale: number;
  spin: number;
  seed: number; // Unsupported by tsParticles
  shouldFadeBackIn?: boolean;
  children?: React.ReactNode;
};

const ParticleView = ({children, ...props}: ParticleViewProps) => {
  const imagePropsList: any[] = [];
  const particleCellPropsList: Omit<ParticleCellProps, 'children'>[] = [];

  const [init, setInit] = useState(false);

  const filteredChildren =
    Children.map(children, (child) => {
      if (isValidElement(child) && child.type === ParticleCell) {
        const {children: _children, ...restProps} = child.props as ParticleCellProps;
        console.debug('ParticleCell children deleted: ', _children);
        particleCellPropsList.push(restProps);
        const imageChild = child.props.children;
        if (isValidElement(imageChild) && imageChild.type === Image) {
          imagePropsList.push(imageChild.props as ImageProps);
          return cloneElement(child, {...child.props, children: null}); // Exclude the Image component
        }
        return null; // Exclude ParticleCell components
      }
      return child; // Include non-ParticleCell components
    }) || []; // Ensure filteredChildren is always an array

  useEffect(() => {
    initParticlesEngine(async (engine: Engine) => {
      await loadFull(engine);
    }).then(() => {
      setInit(true);
    });
  }, []);

  const particlesLoaded = useCallback(async (container) => {
    if (particleCellPropsList[0].lifetime && props.shouldFadeBackIn) {
      const canvasHeight = container.canvas.size.height;

      const fadeOutStart = canvasHeight * 0.2; // 20% from the top
      const fadeOutEnd = canvasHeight * 0.35; // 40% from the top
      const transparentStart = canvasHeight * 0.35; // Fully faded out starts here
      const transparentEnd = canvasHeight * 0.6; // Fully faded out ends here
      const fadeInStart = canvasHeight * 0.6; // Start fading back in
      const fadeInEnd = canvasHeight * 0.85; // Fully visible again

      const updateParticles = () => {
        const particlesArray = container.particles._array;

        particlesArray.forEach((particle) => {
          const y = particle.position.y;

          if (y < fadeOutStart) {
            // Fully visible at the top
            particle.opacity.value = 1;
          } else if (y >= fadeOutStart && y < fadeOutEnd) {
            // Fade out between 20%-40% of the screen
            particle.opacity.value = 1 - (y - fadeOutStart) / (fadeOutEnd - fadeOutStart);
          } else if (y >= transparentStart && y < transparentEnd) {
            // Fully transparent for the middle 25%
            particle.opacity.value = 0;
          } else if (y >= fadeInStart && y < fadeInEnd) {
            // Fade back in between 65%-85% of the screen
            particle.opacity.value = (y - fadeInStart) / (fadeInEnd - fadeInStart);
          } else {
            // Fully visible after 85%
            particle.opacity.value = 1;
          }
        });

        requestAnimationFrame(updateParticles); // Continuously update particles
      };

      updateParticles();
    }
  }, []);

  // Function to generate a random number within a range
  // const getRandomInRange = (min, max) => {
  //   return Math.random() * (max - min) + min;
  // };

  // Function to generate a random RGBA color within specified ranges
  // const generateRandomRGBA = (
  //   calculatedRedRange,
  //   calculatedGreenRange,
  //   calculatedBlueRange,
  //   calculatedAlphaRange,
  // ) => {
  //   const red = getRandomInRange(calculatedRedRange[0], calculatedRedRange[1]);
  //   const green = getRandomInRange(calculatedGreenRange[0], calculatedGreenRange[1]);
  //   const blue = getRandomInRange(calculatedBlueRange[0], calculatedBlueRange[1]);
  //   const alpha = getRandomInRange(calculatedAlphaRange[0], calculatedAlphaRange[1]);

  //   return {
  //     r: Math.floor(red),
  //     g: Math.floor(green),
  //     b: Math.floor(blue),
  //     a: alpha,
  //   };
  // };

  // function radiansToDegrees(radians) {
  //   return radians * (180 / Math.PI);
  // }

  const particlesOptions = useMemo(() => {
    const canvasWidth = Dimensions.get('screen').width;
    const canvasHeight = Dimensions.get('screen').height;

    const positionPercentage = {
      x: (props.emitterPosition.x / canvasWidth) * 100,
      y: (props.emitterPosition.y / canvasHeight) * 100,
    };
    const mergedParticles = {
      particles: {
        size: {
          value: {
            min: imagePropsList[0].style?.width,
            max: imagePropsList[0].style?.height,
          },
        },
        move: {
          enable: true,
          speed: props.velocity,
          angle: {offset: 0, value: 0},
          spread: 0,
          random: true,
          direction: MoveDirection.bottom,
          outModes: {
            default: OutMode.none,
          },
        },
        shape: {
          type: 'image',
          options: {
            image: imagePropsList.map((imgProps) => ({
              src: imgProps?.source?.uri,
              width: imgProps.style?.width || 100, // Default to 100 if not specified
              height: imgProps.style?.height || 100, // Default to 100 if not specified
            })),
          },
        },
        zIndex: {
          value: props.emitterZPosition,
        },
      },
      emitters: {
        position: {
          x: positionPercentage.x,
          y: positionPercentage.y,
        },
        size: {
          width: props.emitterSize?.width || Dimensions.get('screen').width, // Default to 1 if undefined
          height: props.emitterSize?.height || 0, // Default to 1 if undefined
        },
        rate: {
          quantity: props.birthRate,
          delay: props.birthRate < 1 ? 10 : 0.05,
        },
      },
      fullScreen: {
        enable: true,
      },
    };

    particleCellPropsList.forEach((cellProps) => {
      // The following commented out props logic is not yet tested,
      // but it may be pretty close to done. Often you just need to verify the
      // units are correct (tsParticles uses degrees not radians, for example)

      // if (cellProps.color) {
      //   mergedParticles.particles["color"] = {
      //     value: () => {
      //       let redRange = cellProps.redRange || 0;
      //       let greenRange = cellProps.greenRange || 0;
      //       let blueRange = cellProps.blueRange || 0;
      //       let alphaRange = cellProps.alphaRange || 0;
      //       const calculatedRedRange = [
      //         Math.max(0, cellProps.color.r - cellProps.color.r * redRange),
      //         Math.min(255, cellProps.color.r + cellProps.color.r * redRange),
      //       ];
      //       const calculatedGreenRange = [
      //         Math.max(0, cellProps.color.g - cellProps.color.g * greenRange),
      //         Math.min(255, cellProps.color.g + cellProps.color.g * greenRange),
      //       ];
      //       const calculatedBlueRange = [
      //         Math.max(0, cellProps.color.b - cellProps.color.b * blueRange),
      //         Math.min(255, cellProps.color.b + cellProps.color.b * blueRange),
      //       ];
      //       const calculatedAlphaRange = [
      //         Math.max(0, cellProps.color.a - cellProps.color.a * alphaRange),
      //         Math.min(1, cellProps.color.a + cellProps.color.a * alphaRange),
      //       ];
      //       const particleColor = generateRandomRGBA(
      //         calculatedRedRange,
      //         calculatedGreenRange,
      //         calculatedBlueRange,
      //         calculatedAlphaRange,
      //       );
      //       let color = {
      //         r: particleColor.r,
      //         g: particleColor.g,
      //         b: particleColor.b,
      //         a: particleColor.a,
      //       };
      //       let redSpeed = cellProps.redSpeed || 0;
      //       let greenSpeed = cellProps.greenSpeed || 0;
      //       let blueSpeed = cellProps.blueSpeed || 0;
      //       let alphaSpeed = cellProps.alphaSpeed || 0;
      //       if (redSpeed || greenSpeed || blueSpeed || alphaSpeed) {
      //         let animation = {};
      //         if (redSpeed) {
      //           animation["r"] = {
      //             enable: true,
      //             speed: (calculatedRedRange[1] - calculatedRedRange[0]) / redSpeed,
      //             sync: false,
      //           };
      //         }
      //         if (greenSpeed) {
      //           animation["g"] = {
      //             enable: true,
      //             speed: (calculatedGreenRange[1] - calculatedGreenRange[0]) / greenSpeed,
      //             sync: false,
      //           };
      //         }
      //         if (blueSpeed) {
      //           animation["b"] = {
      //             enable: true,
      //             speed: (calculatedBlueRange[1] - calculatedBlueRange[0]) / blueSpeed,
      //             sync: false,
      //           };
      //         }
      //         if (alphaSpeed) {
      //           animation["a"] = {
      //             enable: true,
      //             speed: (calculatedAlphaRange[1] - calculatedAlphaRange[0]) / alphaSpeed,
      //             sync: false,
      //           };
      //         }
      //         color["animation"] = animation;
      //       }
      //       return color;
      //     },
      //     sync: false,
      //   };
      // }
      // if (cellProps.lifetime) {
      //   mergedParticles.particles!.life = {
      //     count: 1,
      //     duration: {
      //       value: cellProps.lifetime,
      //       sync: false,
      //     },
      //   };
      //   mergedParticles.particles!.life.count = 1;
      //   if (cellProps.lifetimeRange) {
      //     mergedParticles.particles!.life.sync = false;
      //     mergedParticles.particles!.life.duration.value = () => {
      //       const minLifetime = Math.max(0, cellProps.lifetime - cellProps.lifetimeRange);
      //       const maxLifetime = cellProps.lifetime + cellProps.lifetimeRange;
      //       return getRandomInRange(minLifetime, maxLifetime);
      //     };
      //   }
      // }

      if (cellProps.birthRate) {
        mergedParticles.emitters!.rate.quantity = cellProps.birthRate;
        mergedParticles.emitters!.rate.delay =
          cellProps.birthRate < 1 ? 0.05 / cellProps.birthRate : 0.05;
      }

      if (cellProps.velocity) {
        const velocityPerFrame = cellProps.velocity / 60;
        const velocityRangePerFrame = cellProps.velocityRange / 60;
        // @ts-ignore
        mergedParticles.particles!.move.speed = {
          min: velocityPerFrame - velocityRangePerFrame,
          max: velocityPerFrame + velocityRangePerFrame,
          sync: false,
        };
      }
      if (cellProps.xAcceleration || cellProps.yAcceleration) {
        // @ts-ignore
        mergedParticles.particles!.move.gravity = {
          enable: true,
          acceleration: cellProps.yAcceleration / 30,
          sync: false,
        };
      }
      if (cellProps.scale) {
        if (cellProps.scaleRange || cellProps.scaleSpeed) {
          // @ts-ignore
          mergedParticles.particles!.size.animation = {
            enable: true,
            speed: cellProps.scaleSpeed * 100,
            minimumValue:
              mergedParticles.particles!.size.value.min -
              mergedParticles.particles!.size.value.min *
                Math.max(0, cellProps.scale - cellProps.scaleRange),
            maximumValue:
              mergedParticles.particles!.size.value.max +
              mergedParticles.particles!.size.value.max * (cellProps.scale + cellProps.scaleRange),
            sync: false,
          };

          mergedParticles.particles!.size.value.min =
            // @ts-ignore
            mergedParticles.particles!.size.animation.minimumValue;
          mergedParticles.particles!.size.value.max =
            // @ts-ignore
            mergedParticles.particles!.size.animation.maximumValue;
        }
      }
      if (cellProps.spin || cellProps.spinRange) {
        // Convert radians to degrees
        const spinInDegrees = cellProps.spin * (180 / Math.PI);
        const spinRangeInDegrees = cellProps.spinRange * (180 / Math.PI);

        // Calculate the range for rotation speed in degrees
        const minSpinSpeed = spinInDegrees - spinRangeInDegrees;
        const maxSpinSpeed = spinInDegrees + spinRangeInDegrees;

        // @ts-ignore
        mergedParticles.particles!.rotate = {
          value: {
            min: 0,
            max: 2 * Math.PI,
          },
          animation: {
            enable: true,
            speed: {
              min: Math.max(minSpinSpeed, 0),
              max: Math.min(maxSpinSpeed, 20), // Clamping so it doesn't look ridiculous
            },
            sync: false, // Each particle will have a unique rotation speed
          },
        };
      }
      if (
        cellProps.emissionLatitude !== undefined &&
        cellProps.emissionLongitude !== undefined &&
        cellProps.emissionRange !== undefined
      ) {
        const latitudeInDegrees = cellProps.emissionLatitude * (180 / Math.PI);
        const longitudeInDegrees = cellProps.emissionLongitude * (180 / Math.PI);
        const rangeInDegrees = cellProps.emissionRange * (180 / Math.PI);
        mergedParticles.particles!.move.spread = 45;
        mergedParticles.particles!.move.random = true;
        // @ts-ignore
        mergedParticles.particles!.move.sync = false;
        mergedParticles.particles!.move.angle = {
          // @ts-ignore
          value: {
            min: latitudeInDegrees - rangeInDegrees / 2,
            max: latitudeInDegrees + rangeInDegrees / 2,
          }, // Vertical tilt
          offset: longitudeInDegrees, // Horizontal direction
        };
      }
    });

    return mergedParticles;
  }, [props, imagePropsList, particleCellPropsList]);

  if (init) {
    return (
      <div style={{position: 'absolute', top: 0, bottom: 0, left: 0, right: 0}}>
        {filteredChildren}
        <Particles
          id='tsparticles'
          particlesLoaded={particlesLoaded}
          options={particlesOptions}
          style={{position: 'absolute', top: 0, bottom: 0, left: 0, right: 0}}
        />
      </div>
    );
  }

  return <></>;
};

export default ParticleView;
